import filter from 'lodash/fp/filter';
import find from 'lodash/fp/find';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import isEmpty from 'lodash/fp/isEmpty';
import join from 'lodash/fp/join';
import map from 'lodash/fp/map';
import set from 'lodash/fp/set';

import { createSelector } from 'reselect';

import { FIRST_QUESTION_ROUTE } from 'Apps/Consultation/constants';
import defaultQuestionSet from 'Apps/Consultation/defaultQuestionSet';
import signinFirstQuestionSet from 'Apps/Consultation/signinFirstQuestionSet';
import supplementsQuestionSet from 'Apps/Consultation/supplementsQuestionSet';

import * as fragrances from 'constants/fragrances';

import { createApiSelectors } from 'utils/reduxUtils';

import { getPrescriptionStatus } from 'dux/prescription/actions';
import { getIsAuthenticated } from 'dux/auth/selectors';
import {
  isPrescriptionPageAbTest,
  isPrescriptionScoringVariantTest,
  isStylingGelFlagActive,
  shouldShowStylingGel,
} from 'dux/featureFlags/selectors';
import * as routerSelectors from 'dux/router/selectors';
import { getHaveAnsweredSupplementsQuestion, getUserHasRedeemableGift } from 'dux/user/selectors';

const getState = state => state.consultation;
const getAnswers = createSelector(getState, state => state.answers);
const getBufferedAnswers = createSelector(getState, state => state.bufferedAnswers);

export const getAnswersData = createSelector(getAnswers, answers => answers.data);

export const getBufferedAnswersData = createSelector(getBufferedAnswers, get('bufferedData'));

export const hasBufferedAnswers = createSelector(
  getBufferedAnswersData,
  bufferedData => !isEmpty(bufferedData)
);

export const getConsultationAnswers = createSelector(
  hasBufferedAnswers,
  getBufferedAnswersData,
  getAnswersData,
  (userHasBufferedAnswers, bufferedAnswers, answersData) =>
    userHasBufferedAnswers ? bufferedAnswers : answersData
);

export const getConsultationProfileCountry = createSelector(
  getConsultationAnswers,
  answersData => answersData?.profile_country
);

export const getConsultationProfileZipcode = createSelector(
  getConsultationAnswers,
  answersData => answersData?.profile_zipcode
);

export const getIsSavingAnswer = createSelector(getAnswers, answers => answers.isSaving);

export const getIsConsultationLoading = createSelector(getAnswers, answers => answers.isLoading);

export const getConsultationError = createSelector(getAnswers, answers => answers.error);

const [getFragrancesData, getFragrancesError, getFragrancesStatus] = createApiSelectors(
  state => state.consultation.fragrances
);

export { getFragrancesData, getFragrancesError, getFragrancesStatus };

export const isFragranceArcadiaAvailable = createSelector(
  getFragrancesData,
  flow(find({ name: fragrances.ARCADIA }), Boolean)
);

const [getFragrancesFeedbackData, , ,] = createApiSelectors(
  state => state.consultation.fragrancesFeedback
);
export { getFragrancesFeedbackData };

export const isFragranceFeedbackArcadiaAvailable = createSelector(
  getFragrancesFeedbackData,
  flow(find({ name: fragrances.ARCADIA }), Boolean)
);

const [getAggressorsData, getAggressorsError, getAggressorsStatus] = createApiSelectors(
  state => state.consultation.aggressors
);
export { getAggressorsData, getAggressorsError, getAggressorsStatus };

const [getIngredientsData, getIngredientsError, getIngredientsStatus] = createApiSelectors(
  state => state.consultation.ingredients
);
export { getIngredientsData, getIngredientsError, getIngredientsStatus };

const [getScoringData, getScoringError, getScoringStatus] = createApiSelectors(
  state => state.consultation.scoring
);
export { getScoringData, getScoringError, getScoringStatus };

export const getResultsStatus = createSelector(
  getIngredientsStatus,
  getScoringStatus,
  getPrescriptionStatus,
  isPrescriptionPageAbTest,
  isPrescriptionScoringVariantTest,
  (
    ingredientsStatus,
    scoringStatus,
    prescriptionStatus,
    isPrescriptionVariant,
    isPrescriptionScoringVariant
  ) => {
    const scoringStatuses = [ingredientsStatus, scoringStatus];
    const scoringPrescriptionStatuses = [ingredientsStatus, prescriptionStatus];
    const prescriptionStatuses = [prescriptionStatus];

    const statuses = !isPrescriptionVariant
      ? isPrescriptionScoringVariant
        ? scoringPrescriptionStatuses
        : scoringStatuses
      : prescriptionStatuses;

    if (statuses.includes('error')) {
      return 'error';
    }

    if (statuses.includes('loading')) {
      return 'loading';
    }

    if (statuses.every(status => status === 'success')) {
      return 'success';
    }

    return 'idle';
  }
);

export const getResultsError = createSelector(
  getIngredientsError,
  getScoringError,
  (ingredientsError, scoringError) => ingredientsError || scoringError
);

export const consultationStepIsStandalone = createSelector(
  routerSelectors.getSearch,
  locationSearch => {
    const params = new URLSearchParams(locationSearch);
    return params.has('standalone');
  }
);

export const userShouldHaveSigninFirst = createSelector(
  getUserHasRedeemableGift,
  routerSelectors.getSearch,
  (userHasRedeemableGift, locationSearch) => {
    const params = new URLSearchParams(locationSearch);
    return (
      userHasRedeemableGift ||
      (params.has('signin-first') && params.get('signin-first') !== 'false')
    );
  }
);

export const userShouldHaveSupplementsQuestion = createSelector(
  getHaveAnsweredSupplementsQuestion,
  routerSelectors.isOnSupplementsQuestionRoute,
  (hasAnsweredSupplementsQuestion, isOnSupplementsQuestionRoute) =>
    !hasAnsweredSupplementsQuestion && isOnSupplementsQuestionRoute
);

export const getCurrentlyLoadedQuestionSet = createSelector(
  userShouldHaveSigninFirst,
  userShouldHaveSupplementsQuestion,
  (shouldHaveSigninFirst, shouldLoadSupplementsQuestion) =>
    shouldLoadSupplementsQuestion
      ? supplementsQuestionSet
      : shouldHaveSigninFirst
      ? signinFirstQuestionSet
      : defaultQuestionSet
);

export const getConsultationQuestionForIndex = createSelector(
  (_state, { questionIndex } = {}) => questionIndex,
  getCurrentlyLoadedQuestionSet,
  shouldShowStylingGel,
  (questionIndex, questions, isStylingGel) => {
    const nextQuestion = questions?.[questionIndex];

    if (nextQuestion?.name === 'styles') {
      const optionsToExcludeInControlVersion = [
        'paste_pomade',
        'gel_jelly',
        'curl_cream',
        'leavein_conditioner',
        'hair_oil',
        'hair_butter',
      ];
      const optionsToExcludeInTestVersion = ['gel_paste', 'leavein_conditioner_curl_cream', 'oil'];

      // ff styling-gel off
      let styleOptions = nextQuestion.options.filter(
        ({ value }) => !optionsToExcludeInControlVersion.includes(value)
      );
      // ff styling-gel on
      if (isStylingGel) {
        styleOptions = nextQuestion.options.filter(
          ({ value }) => !optionsToExcludeInTestVersion.includes(value)
        );
      }
      nextQuestion.options = styleOptions;
    }
    return nextQuestion;
  }
);

const getRouteForQuestionIndex = createSelector(getConsultationQuestionForIndex, get('route'));

export const getTipForQuestion = createSelector(
  getConsultationQuestionForIndex,
  getConsultationAnswers,
  (questionContent, answers) => {
    const filters = get('optionsFilter')(questionContent)?.(answers);
    if (filters && get('name', questionContent) === 'goals') {
      const tipContent = get('tip.content')(questionContent);
      return flow(
        get('tip'),
        set(
          'content',
          flow(
            filter(({ value }) => filters[value]),
            map('content'),
            join('')
          )(tipContent)
        )
      )(questionContent);
    }
    return get('tip')(questionContent);
  }
);
export const getOptionsForQuestion = createSelector(
  getConsultationQuestionForIndex,
  getConsultationAnswers,
  isStylingGelFlagActive,
  (questionContent, answers, isStylingGel) => {
    let options = get('options')(questionContent);
    const filters = answers && get('optionsFilter')(questionContent)?.(answers, isStylingGel);
    if (filters) {
      const predicate = ({ value }) => filters[value];
      options = filter(predicate)(options);
    }
    return options;
  }
);

export const getInitialValue = createSelector(
  getConsultationQuestionForIndex,
  getConsultationAnswers,
  isStylingGelFlagActive,
  (question, answers, isStylingGel) => {
    if (question.getInitialValue && answers) {
      return question.getInitialValue(answers, isStylingGel);
    }

    return question.multi ? [] : null;
  }
);

export const getHairTexture = createSelector(
  getConsultationAnswers,
  /* In case of empty string we wants null or undefined instead */
  answers => answers?.diag_hair_texture || null
);

export const getShouldInitializeConsultation = createSelector(
  getIsAuthenticated,
  getAnswers,
  (userIsAuthenticated, { data, isLoading, didInvalidate, error }) =>
    userIsAuthenticated && ((!data && !isLoading && !error) || didInvalidate)
);

export const shouldTrackConsultationStart = createSelector(
  getRouteForQuestionIndex,
  currentRoute => currentRoute === FIRST_QUESTION_ROUTE
);

export const getEmailCaptureExitConsultationModalOpened = createSelector(
  getAnswers,
  get('emailCaptureExitConsultationModalOpened')
);
